/**
 * calendar-links: what each "Add to calendar" entry DOES (CC 0475). Before this, picking an entry only recorded the
 * choice and closed the menu. Built from the confirmed facts already on the page; nothing is fetched.
 *
 *   google  → opens Google Calendar's TEMPLATE link in a new tab
 *   owa     → opens Outlook on the web's compose deeplink in a new tab
 *   outlook → downloads an .ics (the desktop app opens it)
 *   ics     → downloads an .ics
 * Both .ics entries use the server's SIGNED file when the confirmation carries one (it holds an online event's join
 * link for a seat holder); otherwise a standards-correct VCALENDAR is built here and downloaded from a Blob.
 */
export interface CalendarFacts {
  title: string;
  /** The event's start and end AS STORED: an instant (Z), or the event zone's wall clock with or without an offset. */
  start: string;
  end: string;
  /** The event's own IANA zone (CC 0485). With it, a stored wall clock is read in THIS zone, never the browser's. */
  timeZone?: string | null;
  location?: string;
  description?: string;
  /** Stable identity for the UID (a registration or booking reference). */
  uid?: string;
}
export type CalendarKey = "google" | "owa" | "outlook" | "ics";
export type CalendarAction =
  | { kind: "open"; url: string }
  | { kind: "download-url"; url: string; filename: string }
  | { kind: "download-ics"; text: string; filename: string }
  | { kind: "none"; reason: "no_time" };

/* THE INSTANT (CC 0485, CC 0488). Date.parse reads an offsetless wall clock in the VISITOR's zone, so a calendar
   entry landed at the wrong hour. The rule:
   - A value carrying an explicit offset or "Z" IS the instant, used as given. The event's zone is for display only.
     (On the fall-back night "01:30-07:00" and "01:30-08:00" are different instants an hour apart; the offset says
     which one.)
   - Only an OFFSETLESS wall clock is resolved in the event's zone, DST included, without consulting the host zone.
       Repeated hour (fall back): the EARLIER occurrence is chosen, deterministically, in every zone.
       Skipped hour (spring forward): that wall clock doesn't exist; the offset in force BEFORE the change is applied,
       so it lands one gap later on the clock (LA 02:30 on the spring date → 10:30Z, shown as 03:30 PDT).
   - Offsetless with no zone, or with an unknown zone, is unknown: no_time, never the browser's guess. */
const WALL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const DAY_MS = 86_400_000;
const offsetMs = (at: number, tz: string): number => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(at)).map((x) => [x.type, x.value]));
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second) - Math.floor(at / 1000) * 1000;
};
/** UTC epoch ms of a stored start/end; NaN when it can't be known. See the rule above. */
export function calendarInstant(value: string | null | undefined, timeZone?: string | null): number {
  const s = String(value ?? "").trim();
  if (!s) return NaN;
  if (HAS_OFFSET.test(s)) return Date.parse(s);
  const m = WALL.exec(s);
  const tz = String(timeZone ?? "").trim();
  if (!m || !tz) return NaN;
  try {
    const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0);
    /* Each offset the zone uses within a day either side gives a candidate instant; a candidate is real when the
       zone shows exactly this wall clock at it. Two real ones = the repeated hour: take the earlier. */
    const offsets = new Set([offsetMs(wall - DAY_MS, tz), offsetMs(wall, tz), offsetMs(wall + DAY_MS, tz)]);
    const real = [...offsets].map((o) => wall - o).filter((c) => c + offsetMs(c, tz) === wall).sort((x, y) => x - y);
    if (real.length) return real[0];
    return wall - offsetMs(wall - DAY_MS, tz); // skipped hour
  } catch {
    return NaN; // an unknown zone
  }
}
const startMs = (f: CalendarFacts): number => calendarInstant(f.start, f.timeZone);
const endMs = (f: CalendarFacts): number => calendarInstant(f.end, f.timeZone);
const stampOf = (t: number): string => new Date(t).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
/** 20261120T150000Z, from an epoch ms or a self-describing instant string. */
export const utcStamp = (v: number | string): string => stampOf(typeof v === "number" ? v : Date.parse(v));
const isoUtc = (t: number): string => new Date(t).toISOString().replace(/\.\d{3}Z$/, "Z");
const usable = (f: CalendarFacts): boolean => { const a = startMs(f), b = endMs(f); return Number.isFinite(a) && Number.isFinite(b) && b > a; };

export function googleCalendarUrl(f: CalendarFacts): string {
  const q = new URLSearchParams({ action: "TEMPLATE", text: f.title, dates: stampOf(startMs(f)) + "/" + stampOf(endMs(f)) });
  if (f.location) q.set("location", f.location);
  if (f.description) q.set("details", f.description);
  return "https://calendar.google.com/calendar/render?" + q.toString();
}
export function outlookWebUrl(f: CalendarFacts): string {
  const q = new URLSearchParams({ path: "/calendar/action/compose", rru: "addevent", subject: f.title, startdt: isoUtc(startMs(f)), enddt: isoUtc(endMs(f)) });
  if (f.location) q.set("location", f.location);
  if (f.description) q.set("body", f.description);
  return "https://outlook.office.com/calendar/deeplink/compose?" + q.toString();
}

/** RFC 5545 TEXT escaping, then line folding at 75 octets (approximated by characters). */
const esc = (s: string): string => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
const fold = (line: string): string => { const out: string[] = []; let rest = line; while (rest.length > 75) { out.push(rest.slice(0, 75)); rest = " " + rest.slice(75); } out.push(rest); return out.join("\r\n"); };
export function buildIcs(f: CalendarFacts, now: Date = new Date()): string {
  const uid = (f.uid || stampOf(startMs(f)) + "-" + f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")) + "@jrni";
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//JRNI//Add to calendar//EN", "CALSCALE:GREGORIAN", "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    "UID:" + esc(uid),
    "DTSTAMP:" + stampOf(now.getTime()),
    "DTSTART:" + stampOf(startMs(f)),
    "DTEND:" + stampOf(endMs(f)),
    "SUMMARY:" + esc(f.title),
    ...(f.location ? ["LOCATION:" + esc(f.location)] : []),
    ...(f.description ? ["DESCRIPTION:" + esc(f.description)] : []),
    "END:VEVENT", "END:VCALENDAR",
  ];
  return lines.map(fold).join("\r\n") + "\r\n";
}
export const icsFilename = (title: string): string => (title.trim().replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, " ").slice(0, 80) || "event") + ".ics";

/** The action for one menu entry. `icsUrl` (the server's signed file) wins for both .ics entries when present. */
export function calendarAction(key: CalendarKey, f: CalendarFacts, opts: { icsUrl?: string | null } = {}): CalendarAction {
  if ((key === "outlook" || key === "ics") && opts.icsUrl) return { kind: "download-url", url: opts.icsUrl, filename: icsFilename(f.title) };
  if (!usable(f)) return { kind: "none", reason: "no_time" };
  if (key === "google") return { kind: "open", url: googleCalendarUrl(f) };
  if (key === "owa") return { kind: "open", url: outlookWebUrl(f) };
  return { kind: "download-ics", text: buildIcs(f), filename: icsFilename(f.title) };
}

/** The entries that will act for these facts (CC 0485): the menu offers only these, so no option is inert. With no
 *  known time, only the .ics entries remain, and only when the server's signed file is present. */
export const CALENDAR_KEYS: readonly CalendarKey[] = ["outlook", "owa", "google", "ics"];
export function actingCalendarKeys(f: CalendarFacts, opts: { icsUrl?: string | null } = {}, keys: readonly CalendarKey[] = CALENDAR_KEYS): CalendarKey[] {
  return keys.filter((k) => calendarAction(k, f, opts).kind !== "none");
}

/** Carry the action out. Injected seams so a test can watch them; the defaults are the browser's own. */
export interface CalendarEffects { open: (url: string) => void; download: (href: string, filename: string, revoke?: () => void) => void }
export const browserCalendarEffects: CalendarEffects = {
  open: (url) => { window.open(url, "_blank", "noopener,noreferrer"); },
  download: (href, filename, revoke) => {
    const a = document.createElement("a");
    a.href = href; a.download = filename; a.rel = "noopener"; a.style.display = "none";
    document.body.appendChild(a); a.click(); a.remove();
    if (revoke) setTimeout(revoke, 0);
  },
};
export function performCalendarAction(action: CalendarAction, fx: CalendarEffects = browserCalendarEffects): boolean {
  if (action.kind === "open") { fx.open(action.url); return true; }
  if (action.kind === "download-url") { fx.download(action.url, action.filename); return true; }
  if (action.kind === "download-ics") {
    const url = URL.createObjectURL(new Blob([action.text], { type: "text/calendar;charset=utf-8" }));
    fx.download(url, action.filename, () => URL.revokeObjectURL(url));
    return true;
  }
  return false;
}
